import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Network, Plus, Trash2, UserCheck, Users, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useEmpresa } from "@/hooks/useEmpresa";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function nombreUser(u) {
  if (!u) return "—";
  return `${u.nombre || ""} ${u.apellidos || ""}`.trim() || u.email || `#${u.id}`;
}

function ListaTiposGerencia({ catalogo, seleccionados, onToggle, defaultOpen = false, titulo = "Permisos" }) {
  const [abierto, setAbierto] = useState(defaultOpen);
  const secciones = [
    { id: "solicitudes", titulo: "Solicitudes del trabajador" },
    { id: "modulos", titulo: "Avisos y revisiones de módulos" },
  ];
  const sel = seleccionados || [];
  const labelsMarcados = (catalogo || [])
    .filter((g) => sel.includes(g.id))
    .map((g) => g.label);
  const resumen =
    labelsMarcados.length === 0
      ? "Ninguno marcado"
      : labelsMarcados.length <= 3
        ? labelsMarcados.join(", ")
        : `${labelsMarcados.slice(0, 3).join(", ")} +${labelsMarcados.length - 3}`;

  return (
    <div className="rounded-lg border border-zinc-200">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50"
      >
        {abierto ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-zinc-500" />
        )}
        <span className="font-medium text-zinc-800">{titulo}</span>
        <span className="ml-auto truncate text-xs text-zinc-500">{resumen}</span>
      </button>
      {abierto && (
        <div className="max-h-56 overflow-y-auto border-t border-zinc-100">
          {secciones.map((sec) => {
            const grupos = (catalogo || []).filter((g) => (g.seccion || "solicitudes") === sec.id);
            if (!grupos.length) return null;
            return (
              <div key={sec.id}>
                <div className="border-b border-zinc-100 bg-zinc-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                  {sec.titulo}
                </div>
                <ul className="divide-y divide-zinc-100">
                  {grupos.map((g) => {
                    const on = sel.includes(g.id);
                    return (
                      <li key={g.id}>
                        <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-zinc-50">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-zinc-300"
                            checked={on}
                            onChange={(e) => onToggle(g.id, e.target.checked)}
                          />
                          <span className={on ? "font-medium text-zinc-900" : "text-zinc-700"}>{g.label}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

async function cargarDefaultsTipos() {
  const { data } = await api.get("/ajustes/solicitudes-aprobacion");
  const cfg = data.config || {};
  return (data.catalogo || []).map((g) => g.id).filter((id) => cfg[id]?.gerencia);
}

export default function GrupoGerencia() {
  const { empresa, cargando: cargandoEmp } = useEmpresa();
  const [grupo, setGrupo] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [altaMiembroOpen, setAltaMiembroOpen] = useState(false);
  const [nuevoMiembroId, setNuevoMiembroId] = useState("");
  const [nuevoMiembroTipos, setNuevoMiembroTipos] = useState([]);
  const [cargandoDefaults, setCargandoDefaults] = useState(false);

  const [altaEncOpen, setAltaEncOpen] = useState(false);
  const [miembroParaEnc, setMiembroParaEnc] = useState(null);
  // 2.º nivel: encargado (usuario) al que se van a añadir encargados o trabajadores supervisados
  const [supervisorParaEnc, setSupervisorParaEnc] = useState(null);
  const [nuevoEncId, setNuevoEncId] = useState("");
  const [nuevoEncTipos, setNuevoEncTipos] = useState([]);

  const catalogo = grupo?.catalogo_tipos || [];
  const moduloActivo = !!empresa?.gestionar_gerencia;
  const adminId = grupo?.administrador_id || grupo?.aprobador_id;

  const cargar = useCallback(async () => {
    if (!moduloActivo) {
      setGrupo(null);
      setCandidatos([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    try {
      const [gRes, cRes] = await Promise.all([
        api.get("/grupo-gerencia"),
        api.get("/grupo-gerencia/candidatos"),
      ]);
      setGrupo(gRes.data);
      setCandidatos(cRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [moduloActivo]);

  useEffect(() => {
    if (!cargandoEmp) cargar();
  }, [cargar, cargandoEmp]);

  const disponiblesAdmin = useMemo(
    () => candidatos.filter((c) => !c.es_miembro && !c.es_encargado_gerencia),
    [candidatos]
  );
  const disponiblesMiembro = useMemo(
    () => candidatos.filter((c) => !c.es_miembro && !c.es_administrador && !c.es_encargado_gerencia),
    [candidatos]
  );
  const disponiblesEncargado = useMemo(
    () =>
      candidatos.filter(
        (c) =>
          !c.es_miembro &&
          !c.es_administrador &&
          !c.es_encargado_gerencia &&
          c.id !== miembroParaEnc?.usuario_id
      ),
    [candidatos, miembroParaEnc]
  );

  const setAdministrador = async (id) => {
    try {
      const { data } = await api.put("/grupo-gerencia/administrador", {
        administrador_id: id ? Number(id) : null,
      });
      setGrupo(data);
      toast.success("Administrador del grupo actualizado");
      const cRes = await api.get("/grupo-gerencia/candidatos");
      setCandidatos(cRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirAltaMiembro = async () => {
    setNuevoMiembroId("");
    setNuevoMiembroTipos([]);
    setAltaMiembroOpen(true);
    setCargandoDefaults(true);
    try {
      setNuevoMiembroTipos(await cargarDefaultsTipos());
    } catch {
      setNuevoMiembroTipos([]);
    } finally {
      setCargandoDefaults(false);
    }
  };

  const confirmarAltaMiembro = async () => {
    if (!nuevoMiembroId) {
      toast.error("Selecciona un trabajador");
      return;
    }
    try {
      const { data } = await api.post("/grupo-gerencia/miembros", {
        usuario_id: Number(nuevoMiembroId),
        tipos_gestion: nuevoMiembroTipos,
      });
      setGrupo(data);
      setAltaMiembroOpen(false);
      toast.success("Miembro de Gerencia añadido");
      const cRes = await api.get("/grupo-gerencia/candidatos");
      setCandidatos(cRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleTipoMiembro = async (usuarioId, tipoId, checked) => {
    const m = (grupo?.miembros || []).find((x) => x.usuario_id === usuarioId);
    if (!m) return;
    const set = new Set(m.tipos_gestion || []);
    if (checked) set.add(tipoId);
    else set.delete(tipoId);
    try {
      const { data } = await api.patch(`/grupo-gerencia/miembros/${usuarioId}`, {
        tipos_gestion: [...set],
      });
      setGrupo(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const quitarMiembro = async (usuarioId) => {
    if (!window.confirm("¿Quitar este miembro de Gerencia? También se quitarán sus encargados asignados.")) return;
    try {
      const { data } = await api.delete(`/grupo-gerencia/miembros/${usuarioId}`);
      setGrupo(data);
      toast.success("Miembro eliminado");
      const cRes = await api.get("/grupo-gerencia/candidatos");
      setCandidatos(cRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirAltaEncargado = async (miembro, supervisor = null) => {
    setMiembroParaEnc(miembro);
    setSupervisorParaEnc(supervisor);
    setNuevoEncId("");
    setNuevoEncTipos([]);
    setAltaEncOpen(true);
    setCargandoDefaults(true);
    try {
      setNuevoEncTipos(await cargarDefaultsTipos());
    } catch {
      setNuevoEncTipos([]);
    } finally {
      setCargandoDefaults(false);
    }
  };

  const confirmarAltaEncargado = async () => {
    if (!miembroParaEnc || !nuevoEncId) {
      toast.error("Selecciona un encargado");
      return;
    }
    try {
      const { data } = await api.post(`/grupo-gerencia/miembros/${miembroParaEnc.usuario_id}/encargados`, {
        encargado_id: Number(nuevoEncId),
        tipos_gestion: nuevoEncTipos,
        supervisor_id: supervisorParaEnc ? supervisorParaEnc.encargado_id : null,
      });
      setGrupo(data);
      setAltaEncOpen(false);
      toast.success(supervisorParaEnc ? "Añadido a la supervisión del encargado" : "Encargado asignado");
      const cRes = await api.get("/grupo-gerencia/candidatos");
      setCandidatos(cRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleTipoEncargado = async (miembroUsuarioId, encargadoId, tipoId, checked) => {
    const m = (grupo?.miembros || []).find((x) => x.usuario_id === miembroUsuarioId);
    const enc = (m?.encargados || []).find((e) => e.encargado_id === encargadoId);
    if (!enc) return;
    const set = new Set(enc.tipos_gestion || []);
    if (checked) set.add(tipoId);
    else set.delete(tipoId);
    try {
      const { data } = await api.patch(
        `/grupo-gerencia/miembros/${miembroUsuarioId}/encargados/${encargadoId}`,
        { tipos_gestion: [...set] }
      );
      setGrupo(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const quitarEncargado = async (miembroUsuarioId, encargadoId, supervisados = 0) => {
    const aviso = supervisados
      ? `¿Quitar a este encargado? Las ${supervisados} persona(s) que supervisa pasarán a depender directamente del miembro de Gerencia.`
      : "¿Quitar este encargado de la supervisión?";
    if (!window.confirm(aviso)) return;
    try {
      const { data } = await api.delete(
        `/grupo-gerencia/miembros/${miembroUsuarioId}/encargados/${encargadoId}`
      );
      setGrupo(data);
      toast.success("Encargado quitado");
      const cRes = await api.get("/grupo-gerencia/candidatos");
      setCandidatos(cRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (cargandoEmp || cargando) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-900" />
      </div>
    );
  }

  if (!moduloActivo) {
    return (
      <div className="mx-auto max-w-2xl space-y-3 p-6">
        <h1 className="text-xl font-semibold text-zinc-900">Grupo Gerencia</h1>
        <p className="text-sm text-zinc-600">
          Este módulo no está activo. Actívalo desde la plataforma (Empresas → Gerencia).
        </p>
        <Button asChild variant="outline">
          <Link to="/admin/ajustes">Ir a Ajustes</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <div>
        <div className="mb-1 flex items-center gap-2 text-indigo-600">
          <Network className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wide">Organización</span>
        </div>
        <h1 className="text-xl font-semibold text-zinc-900">Grupo Gerencia</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Administrador → miembros de Gerencia → encargados → (opcional) encargados supervisados por otro encargado.
          En cada nivel marcas qué tipos de solicitud gestiona el superior; cada solicitud la ve y la resuelve
          solo la persona asignada. Lo no marcado sigue con el flujo normal del departamento.
        </p>
      </div>

      <section className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-zinc-800">
          <UserCheck className="h-4 w-4 text-indigo-600" />
          Administrador del Grupo Gerencia
        </div>
        <Select
          value={adminId ? String(adminId) : "none"}
          onValueChange={(v) => setAdministrador(v === "none" ? null : v)}
        >
          <SelectTrigger className="max-w-md">
            <SelectValue placeholder="Sin administrador" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sin administrador</SelectItem>
            {disponiblesAdmin.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {nombreUser(c)} ({c.rol})
              </SelectItem>
            ))}
            {grupo?.administrador && !disponiblesAdmin.some((c) => c.id === adminId) && (
              <SelectItem value={String(adminId)}>{nombreUser(grupo.administrador)}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <p className="mt-2 text-xs text-zinc-500">
          Supervisa a los miembros de Gerencia. Puede ser el admin de la empresa u otro trabajador.
        </p>
      </section>

      <section className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-medium text-zinc-800">
            <Users className="h-4 w-4 text-indigo-600" />
            Miembros de Gerencia
          </h2>
          <Button size="sm" onClick={abrirAltaMiembro} className="gap-1">
            <Plus className="h-4 w-4" /> Añadir miembro
          </Button>
        </div>

        {(grupo?.miembros || []).length === 0 ? (
          <p className="text-sm text-zinc-500">
            Aún no hay miembros. Añade trabajadores de Gerencia; luego asígnales los encargados que supervisan.
          </p>
        ) : (
          <div className="space-y-5">
            {(grupo.miembros || []).map((m) => (
              <div key={m.id} className="rounded-lg border border-zinc-200 p-4">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-zinc-900">{nombreUser(m.usuario)}</p>
                    <p className="text-xs text-zinc-500">
                      {m.usuario?.departamento || "Sin depto"} · {m.usuario?.rol}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => quitarMiembro(m.usuario_id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <ListaTiposGerencia
                  catalogo={catalogo}
                  seleccionados={m.tipos_gestion || []}
                  titulo="Permisos del administrador del Grupo Gerencia"
                  onToggle={(tipoId, checked) => toggleTipoMiembro(m.usuario_id, tipoId, checked)}
                />

                <div className="mt-4 border-t border-zinc-100 pt-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-medium text-zinc-700">Encargados que supervisa</p>
                    <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => abrirAltaEncargado(m)}>
                      <Plus className="h-3.5 w-3.5" /> Añadir encargado
                    </Button>
                  </div>

                  {(m.encargados || []).filter((e) => !e.supervisor_id).length === 0 ? (
                    <p className="text-xs text-zinc-500">Ningún encargado asignado todavía.</p>
                  ) : (
                    <div className="space-y-3">
                      {(m.encargados || [])
                        .filter((e) => !e.supervisor_id)
                        .map((enc) => {
                          const supervisados = (m.encargados || []).filter(
                            (x) => x.supervisor_id === enc.encargado_id
                          );
                          return (
                            <div
                              key={enc.id}
                              className="rounded-md border border-zinc-100 bg-zinc-50/50 p-3"
                              data-testid={`encargado-${enc.encargado_id}`}
                            >
                              <div className="mb-2 flex items-start justify-between gap-2">
                                <div>
                                  <p className="text-sm font-medium text-zinc-900">{nombreUser(enc.encargado)}</p>
                                  <p className="text-[11px] text-zinc-500">
                                    {enc.encargado?.departamento || "Sin depto"} · {enc.encargado?.rol}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 h-8"
                                  onClick={() => quitarEncargado(m.usuario_id, enc.encargado_id, supervisados.length)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              <ListaTiposGerencia
                                catalogo={catalogo}
                                seleccionados={enc.tipos_gestion || []}
                                titulo="Permisos sobre el encargado"
                                onToggle={(tipoId, checked) =>
                                  toggleTipoEncargado(m.usuario_id, enc.encargado_id, tipoId, checked)
                                }
                              />

                              <div className="mt-3 border-t border-zinc-200 pt-3">
                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                  <p className="text-[11px] font-medium text-zinc-600">
                                    Encargados y trabajadores que supervisa {nombreUser(enc.encargado)}
                                  </p>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 gap-1 text-xs"
                                    onClick={() => abrirAltaEncargado(m, enc)}
                                    data-testid={`anadir-supervisado-${enc.encargado_id}`}
                                  >
                                    <Plus className="h-3 w-3" /> Añadir
                                  </Button>
                                </div>
                                {supervisados.length === 0 ? (
                                  <p className="text-[11px] text-zinc-500">Ninguno todavía.</p>
                                ) : (
                                  <div className="space-y-2 border-l-2 border-indigo-100 pl-3">
                                    {supervisados.map((sub) => (
                                      <div
                                        key={sub.id}
                                        className="rounded-md border border-zinc-100 bg-white p-2.5"
                                        data-testid={`supervisado-${sub.encargado_id}`}
                                      >
                                        <div className="mb-2 flex items-start justify-between gap-2">
                                          <div>
                                            <p className="text-sm font-medium text-zinc-900">
                                              {nombreUser(sub.encargado)}
                                            </p>
                                            <p className="text-[11px] text-zinc-500">
                                              {sub.encargado?.departamento || "Sin depto"} · {sub.encargado?.rol}
                                            </p>
                                          </div>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 text-red-600"
                                            onClick={() => quitarEncargado(m.usuario_id, sub.encargado_id)}
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </Button>
                                        </div>
                                        <ListaTiposGerencia
                                          catalogo={catalogo}
                                          seleccionados={sub.tipos_gestion || []}
                                          titulo={`Permisos de ${nombreUser(enc.encargado)} sobre esta persona`}
                                          onToggle={(tipoId, checked) =>
                                            toggleTipoEncargado(m.usuario_id, sub.encargado_id, tipoId, checked)
                                          }
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={altaMiembroOpen} onOpenChange={setAltaMiembroOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Añadir miembro de Gerencia</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Trabajador</Label>
              <Select value={nuevoMiembroId} onValueChange={setNuevoMiembroId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  {disponiblesMiembro.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {nombreUser(c)} ({c.rol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Tipos que gestiona el administrador del Grupo Gerencia</Label>
              <p className="mb-2 text-xs text-zinc-500">Premarcados según Ajustes → Grupo Gerencia.</p>
              {cargandoDefaults ? (
                <p className="text-sm text-zinc-400">Cargando…</p>
              ) : (
                <ListaTiposGerencia
                  catalogo={catalogo}
                  seleccionados={nuevoMiembroTipos}
                  defaultOpen
                  titulo="Tipos a gestionar"
                  onToggle={(tipoId, checked) =>
                    setNuevoMiembroTipos((prev) =>
                      checked ? [...prev, tipoId] : prev.filter((x) => x !== tipoId)
                    )
                  }
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAltaMiembroOpen(false)}>Cancelar</Button>
            <Button onClick={confirmarAltaMiembro} disabled={cargandoDefaults}>Añadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={altaEncOpen} onOpenChange={setAltaEncOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {supervisorParaEnc
                ? `Añadir a la supervisión de ${nombreUser(supervisorParaEnc.encargado)}`
                : `Encargado para ${miembroParaEnc ? nombreUser(miembroParaEnc.usuario) : "…"}`}
            </DialogTitle>
          </DialogHeader>
          {supervisorParaEnc && (
            <p className="text-xs text-zinc-500">
              Las solicitudes de la persona que añadas las gestionará {nombreUser(supervisorParaEnc.encargado)},
              no el miembro de Gerencia. Los trabajadores de su departamento que no añadas siguen con su encargado.
            </p>
          )}
          <div className="space-y-3">
            <div>
              <Label>Encargado / trabajador a supervisar</Label>
              <Select value={nuevoEncId} onValueChange={setNuevoEncId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  {disponiblesEncargado.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {nombreUser(c)} ({c.rol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Qué gestiona de este encargado</Label>
              <p className="mb-2 text-xs text-zinc-500">
                Premarcados según Ajustes. El encargado deja de autoaprobar / recibir esos tipos.
              </p>
              {cargandoDefaults ? (
                <p className="text-sm text-zinc-400">Cargando…</p>
              ) : (
                <ListaTiposGerencia
                  catalogo={catalogo}
                  seleccionados={nuevoEncTipos}
                  defaultOpen
                  titulo="Tipos a gestionar"
                  onToggle={(tipoId, checked) =>
                    setNuevoEncTipos((prev) =>
                      checked ? [...prev, tipoId] : prev.filter((x) => x !== tipoId)
                    )
                  }
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAltaEncOpen(false)}>Cancelar</Button>
            <Button onClick={confirmarAltaEncargado} disabled={cargandoDefaults}>Añadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
